import * as Hooks from 'preact/hooks';
import * as Logging from '../../store/actions/logging';
import { useDispatch } from 'react-redux';

// Use latest to always have a valid response if the request has been used at least once (potentially stale data)
// use results to ensure the response is the most up-to-date response which will be undefined while the request is in-flight.
export type AsyncResult<U> = {
    result?: U;
    latest?: U;
    error?: unknown;
    retry: () => void;
};

export function useAsync<T>(f: () => Promise<T>, inputs?: unknown[]): AsyncResult<T> {
    const dispatch = useDispatch();
    const [result, setResult] = Hooks.useState<T | undefined>(undefined);
    const [latest, setLatest] = Hooks.useState<T | undefined>(undefined);
    const [error, setError] = Hooks.useState<unknown | undefined>(undefined);
    const [retryWatch, setRetryWatch] = Hooks.useState<{}>({});
    Hooks.useEffect(() => {
        let superseded = false;

        f().then(
            result => {
                if (!superseded) {
                    setResult(result);
                    setLatest(result);
                }
            },
            error => {
                dispatch(Logging.logError(error));
                if (!superseded) setError(error);
            },
        );

        return () => {
            superseded = true;
            setResult(undefined);
            setError(undefined);
        };
    }, [retryWatch, ...(inputs ?? [])]);

    return {
        result,
        latest,
        error,
        retry: () => {
            setError(undefined);
            setRetryWatch({});
        },
    };
}

export function useAsyncValues<T>(valuePromises: Array<Promise<T>>): Array<T> {
    const [values, setValues] = Hooks.useState<Array<T>>([]);

    Hooks.useEffect(() => {
        let superseded = false;
        valuePromises.forEach(p =>
            p.then(value => {
                if (!superseded) {
                    setValues(prev => [...prev, value]);
                }
            }),
        );
        return () => {
            superseded = true;
            setValues([]);
        };
    }, [valuePromises]);

    return values;
}
